<script setup lang="ts">
  import { computed } from 'vue';
  import type { TrainingStatus, TrainingType } from '@/entities/Training';
  import { ColoredButton } from '@/shared/ui';
  import CheckCircleIcon from '@/shared/assets/icons/CheckCircleIcon.svg';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';
  import CertificateIcon from '@/shared/assets/icons/CertificateIcon.svg';
  import { isTabbedTrainingType } from '../../lib/isTabbedTrainingType';

  const props = withDefaults(
    defineProps<{
      status: TrainingStatus;
      type: TrainingType;
      /** Instant of an explicit completion; null when the training was never completed. */
      completedAt: string | null;
      isExporting?: boolean;
      isGeneratingCertificates?: boolean;
    }>(),
    { isExporting: false, isGeneratingCertificates: false }
  );

  const emit = defineEmits<{
    (e: 'complete'): void;
    (e: 'export'): void;
    (e: 'generate-certificates'): void;
  }>();

  /**
   * Exporting is offered on every type and never depends on the status; the button
   * only goes inert while its own request is in flight.
   */
  function requestExport(): void {
    if (props.isExporting) return;

    emit('export');
  }

  const isTabbedType = computed(() => isTabbedTrainingType(props.type));

  /**
   * The derived status reads COMPLETED for every training whose last session has
   * passed, so it cannot gate the button: doing so left it inert on the training
   * that still has pending collaborators to promote, which is exactly the case the
   * feature exists for.
   *
   * Only an explicit completion has nothing left to do, so only it disables. The
   * endpoint is idempotent, so the worst case of an enabled button on an already
   * completed training is a no-op, not a double promotion.
   */
  const isExplicitlyCompleted = computed(() => props.completedAt !== null);

  /**
   * Certificates keep reading the status on purpose: they are offered for a training
   * the screen shows as Concluído, however it got there.
   */
  const isCompleted = computed(() => props.status === 'COMPLETED');

  /**
   * Generating certificates only exists on the tabbed types, and only once the
   * training is completed.
   */
  const isCompletedTabbedTraining = computed(() => isTabbedType.value && isCompleted.value);
</script>

<template>
  <div
    data-testid="view-training-actions-bar"
    class="flex flex-wrap gap-3"
  >
    <ColoredButton
      data-testid="btn-concluir-treinamento"
      text="Concluir treinamento"
      :icon="CheckCircleIcon"
      icon-alt="Ícone de conclusão"
      :disabled="isExplicitlyCompleted"
      @click="emit('complete')"
    />
    <ColoredButton
      v-if="isTabbedType"
      data-testid="btn-gerar-certificados"
      text="Gerar Certificados"
      :icon="CertificateIcon"
      icon-alt="Ícone de certificado"
      :disabled="!isCompletedTabbedTraining || isGeneratingCertificates"
      @click="emit('generate-certificates')"
    />
    <ColoredButton
      data-testid="btn-exportar-excel"
      text="Exportar para Excel"
      :icon="ExportExcelIcon"
      icon-alt="Ícone de exportação"
      :disabled="props.isExporting"
      @click="requestExport"
    />
  </div>
</template>
